import React, { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { Commerce } from './ShopCommerce';
import { CartLineList } from './CartLines';
import { cartMoney } from './cartMoney';
import './checkoutLayout.css';
import { resolveCheckoutLabels, subtotalLabel } from '../components/checkoutLabels';
import { shopWords } from './shopWords';

// The checkout laid out as Shopify's: the order form, and beside it a panel with the order's summary. It
// reads the width it is given rather than the window's, so the same element sits as two columns on the
// checkout page and folds on a phone or in a product page's narrow column — there the summary becomes a
// "Show order summary" bar above the form, its total always in view.

// Lays the form beside the order's summary, folding the summary above the form where the column is narrow.
export const CheckoutLayout: React.FC<React.PropsWithChildren<{ commerce: Commerce }>> = ({ commerce, children }) => {
  const [open, setOpen] = useState(false);
  const summaryId = useId();
  const count = commerce.lines.reduce((sum, line) => sum + line.quantity, 0);
  const subtotal = commerce.quote ? cartMoney(commerce.quote.subtotal, commerce.currency, commerce.direction) : '';
  const words = shopWords(commerce.language);
  return (
    <div className="xe-checkout">
      <div className="xe-checkout-cols">
        <aside className="xe-checkout-aside" aria-label={words.orderSummary}>
          <button type="button" className="xe-checkout-toggle" aria-expanded={open} aria-controls={summaryId} onClick={() => setOpen(!open)}>
            <span>{open ? words.hideSummary : words.showSummary}<ChevronDown size={16} aria-hidden="true" /></span>
            <strong>{subtotal}</strong>
          </button>
          <div className="xe-checkout-summary" id={summaryId} data-open={open || undefined}>
            <h2 className="xe-checkout-summary-title">{words.orderSummary}</h2>
            <CartLineList commerce={commerce} editable={false} />
            <p className="xe-checkout-subtotal"><span>{subtotalLabel(resolveCheckoutLabels(undefined, commerce.language ?? 'en'), count)}</span><strong>{subtotal}</strong></p>
          </div>
        </aside>
        <div className="xe-checkout-main">{children}</div>
      </div>
    </div>
  );
};
